import React from 'react';
import { Video as LucideIcon, Clock } from 'lucide-react';
import { formatInTz } from '../../utils/dateFormatters';
import { useTimezone } from '../../hooks/useTimezone';
import type { TaskFeedItem } from '../../hooks/useTasksQueue';

interface ActivityFeedItemProps {
  activity: TaskFeedItem;
  icon: LucideIcon;
  iconColor: string;
  description: string;
  isHighlighted?: boolean;
}

const ActivityFeedItem: React.FC<ActivityFeedItemProps> = ({
  activity,
  icon: Icon,
  description,
  isHighlighted = false,
}) => {
  const tz = useTimezone();
  const isProcessing = activity.status === 'processing';

  return (
    <div className="relative">
      <div
        className={`flex items-center justify-between p-3.5 rounded-xl transition-all duration-300 border ${
          isHighlighted
            ? 'bg-gradient-to-r from-[#05E0D1]/10 to-[#1875FF]/10 border-[#05E0D1] shadow-lg'
            : 'bg-gradient-to-r from-white to-[#1875FF]/5 border-[#1875FF]/20 hover:from-[#05E0D1]/10 hover:to-[#1875FF]/10 hover:border-[#05E0D1] hover:shadow-lg'
        }`}
      >
        <div className="flex items-center space-x-3 flex-1 min-w-0">
          <div className="w-10 h-10 bg-gradient-to-br from-[#1875FF]/20 to-[#05E0D1]/20 rounded-full flex items-center justify-center shadow-md flex-shrink-0">
            <Icon className="text-[#1875FF]" size={18} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-gray-900 truncate">{activity.leadName}</p>
            <p className="text-xs text-gray-600 truncate">{activity.company}</p>
            <p className="text-xs text-gray-700 flex items-center space-x-1 mt-0.5">
              <Clock size={11} />
              <span>{formatInTz(activity.timestamp, 'HH:mm', tz)}</span>
            </p>
          </div>
          <div className="flex-shrink-0 flex items-center space-x-2">
            {isProcessing && (
              <div className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#05E0D1] opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-[#05E0D1]"></span>
              </div>
            )}
            <span className="text-xs font-semibold text-[#1875FF] bg-[#1875FF]/10 px-2 py-1 rounded">
              {description}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActivityFeedItem;
